import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession,
  getCurrentUser as amplifyGetCurrentUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import type { AuthProvider, CognitoClaims, SignInResult } from './types';
import type { AuthUser, UserRole } from '@/types/auth';
import { COGNITO_CONFIG } from '@/lib/utils';

/**
 * Configure Amplify for SRP direct auth (no OAuth redirect).
 * Only userPoolId and userPoolClientId are needed — no loginWith.oauth block.
 */
function configureAmplify(): void {
  if (!COGNITO_CONFIG.userPoolId || !COGNITO_CONFIG.userPoolClientId) {
    if (import.meta.env.DEV) console.warn('Cognito configuration missing. Auth will not work.');
    return;
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: COGNITO_CONFIG.userPoolId,
        userPoolClientId: COGNITO_CONFIG.userPoolClientId,
        // Kein loginWith.oauth — SRP Direct Auth, kein Redirect-Flow
      },
    },
  });
}

/**
 * Parse Cognito ID token claims into AuthUser.
 */
function parseUserFromClaims(claims: CognitoClaims): AuthUser {
  const role = (claims['custom:role'] as UserRole) || 'merchant_viewer';
  const merchantId = claims['custom:merchant_id'] || null;

  return {
    userId: claims.sub,
    email: claims.email,
    role,
    merchantId: role === 'platform_admin' ? null : merchantId,
  };
}

/**
 * SRP Direct Auth implementation.
 * Authenticates directly against Cognito via Amplify SDK — kein OAuth Redirect.
 *
 * @see Spec 0043 — Custom Login Flow
 */
class CognitoDirectAuth implements AuthProvider {
  private initialized = false;
  private listeners: Set<(user: AuthUser | null) => void> = new Set();

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (this.initialized) return;

    configureAmplify();

    // Hub-Events fuer Auth-State-Changes — nur relevante Events
    Hub.listen('auth', ({ payload }: { payload: { event: string; data?: unknown } }) => {
      switch (payload.event) {
        case 'signedIn':
          this.notifyListeners();
          break;
        case 'signedOut':
          this.notifyListeners();
          break;
        case 'tokenRefresh':
          this.notifyListeners();
          break;
        case 'tokenRefresh_failure':
          if (import.meta.env.DEV) console.error('Token refresh failed:', payload.data);
          this.notifyListeners();
          break;
      }
    });

    this.initialized = true;
  }

  private async notifyListeners(): Promise<void> {
    const user = await this.getCurrentUser();
    this.listeners.forEach((callback) => callback(user));
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    const result = await amplifySignIn({ username: email, password });

    if (result.nextStep.signInStep === 'DONE') {
      // Tokens direkt im Amplify-Storage — kein Callback noetig
      await this.notifyListeners();
      return { success: true };
    }

    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { success: false, challenge: 'NEW_PASSWORD_REQUIRED' };
    }

    // Weitere Challenges (MFA etc.) — Phase 2
    if (import.meta.env.DEV) {
      console.warn('Unhandled sign-in step:', result.nextStep.signInStep);
    }
    return { success: false, challenge: result.nextStep.signInStep as SignInResult['challenge'] };
  }

  async signOut(): Promise<void> {
    try {
      // global: true invalidiert alle Tokens server-seitig (Spec 0043 AE-05)
      await amplifySignOut({ global: true });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sign out error:', error);
      // Lokaler State wird trotzdem bereinigt durch Hub-Event
    }
  }

  async resetPassword(email: string): Promise<void> {
    await amplifyResetPassword({ username: email });
  }

  async confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await amplifyConfirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }

  async confirmNewPassword(newPassword: string): Promise<void> {
    // Loest die NEW_PASSWORD_REQUIRED Challenge
    const result = await amplifyConfirmSignIn({ challengeResponse: newPassword });

    if (result.nextStep.signInStep === 'DONE') {
      await this.notifyListeners();
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) {
        return null;
      }

      const claims = idToken.payload as unknown as CognitoClaims;
      return parseUserFromClaims(claims);
    } catch {
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await amplifyGetCurrentUser();
      const session = await fetchAuthSession();
      return !!session.tokens?.accessToken;
    } catch {
      return false;
    }
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
    this.listeners.add(callback);

    // Sofort mit aktuellem State benachrichtigen
    this.getCurrentUser().then(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }
}

export const cognitoDirectProvider = new CognitoDirectAuth();
